import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  viewTechnician,
  ViewTechnicianApiResponse,
  ViewTechnician,
} from '../api/viewTechnicianApiService';
import {
  getStatusLabel as getSharedStatusLabel,
  getStatusBadgeClasses as getSharedStatusBadgeClasses,
} from '@/shared/utils/statusBadge';

export const PROFILE_NAV_ITEMS = [
  { id: 'PodstawoweInformacje', label: 'Podstawowe informacje' },
  { id: 'ComplianceDetails', label: 'Dane zgodności' },
  { id: 'Facilities', label: 'Placówki' },
  { id: 'Permissions', label: 'Uprawnienia' },
] as const;

export const getTechnicianInitials = (
  name: string | null | undefined
): string => {
  if (!name) {
    return '—';
  }
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

export const getStatusLabel = (
  status: ViewTechnician['status'] | null | undefined
): string => {
  return getSharedStatusLabel(status);
};

export const getStatusBadgeClasses = (
  status: ViewTechnician['status'] | null | undefined
): { container: string; dot: string } => {
  return getSharedStatusBadgeClasses(status);
};

export interface UseViewTechnicianReturn {
  technicianData: ViewTechnicianApiResponse | undefined;
  technician: ViewTechnician | undefined;
  isLoading: boolean;
  showErrorScreen: boolean;
  initials: string;
  statusLabel: string;
  statusBadgeClasses: { container: string; dot: string };
  refetch: () => Promise<void>;
}

export const useViewTechnician = (
  technicianId: string
): UseViewTechnicianReturn => {
  const [technicianData, setTechnicianData] = useState<
    ViewTechnicianApiResponse | undefined
  >(undefined);
  const [showErrorScreen, setShowErrorScreen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchTechnicianData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await viewTechnician({ technicianId });
      if (response.success && response.data) {
        setTechnicianData(response.data);
        setShowErrorScreen(false);
      } else {
        setShowErrorScreen(true);
      }
    } catch {
      setShowErrorScreen(true);
    } finally {
      setIsLoading(false);
    }
  }, [technicianId]);

  useEffect(() => {
    fetchTechnicianData();
  }, [fetchTechnicianData]);

  const technician = useMemo(
    () => technicianData?.technician,
    [technicianData]
  );

  const initials = useMemo(
    () => getTechnicianInitials(technician?.name),
    [technician?.name]
  );

  const statusLabel = useMemo(
    () => getStatusLabel(technician?.status),
    [technician?.status]
  );

  const statusBadgeClasses = useMemo(
    () => getStatusBadgeClasses(technician?.status),
    [technician?.status]
  );

  return {
    technicianData,
    technician,
    isLoading,
    showErrorScreen,
    initials,
    statusLabel,
    statusBadgeClasses,
    refetch: fetchTechnicianData,
  };
};
